import { useEffect, useMemo, useRef, useState } from "react"
import { useApolloClient, type ApolloError } from "@apollo/client"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { errorToast } from "@/v2-ui/toast"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { DotText } from "@/v2-backoffice-client/components/dot"
import type { ColumnMetaMap } from "@/v2-backoffice-client/components/table/table.types"
import TranslationAiFillAction from "@/v2-backoffice-client/translation/catalog/TranslationAiFillAction"
import TranslationDeleteRowsAction from "@/v2-backoffice-client/translation/deleteRows/TranslationDeleteRowsAction"
import {
  patchTranslationCatalogCache,
  type CatalogCachePatch,
} from "@/v2-backoffice-client/translation/catalog/translation.catalog.cache"
import TranslationCatalogRowDetail from "@/v2-backoffice-client/translation/catalog/TranslationCatalogRowDetail"
import {
  OBJECT_TYPE_LABEL,
  OBJECT_TYPE_LABEL_PLURAL,
  TRANSLATION_KIND_PARAM,
} from "@/v2-backoffice-client/translation/translation.constants"
import type { SavedTranslationText } from "@/v2-backoffice-client/translation/editor/translation.editor.utils"
import type {
  SupportedLanguage,
  TranslationBoard,
  TranslationRow,
} from "@/v2-backoffice-client/translation/translation.types"

const COLUMN_SIZING = {
  category: 200,
  code: 320,
  missingCount: 90,
}

// One column per supported language, built from the live language list
// (common.Languages.IsSupported) rather than a fixed nb/en/sv set, so a new
// language (e.g. nn-NO) gets a column automatically once it is supported. The
// value comes from the row's full `texts` set keyed by language code.
function createLanguageColumns(
  languages: SupportedLanguage[],
): ColumnMetaMap<TranslationRow> {
  const columns: ColumnMetaMap<TranslationRow> = {}
  for (const language of languages) {
    columns[language.code] = {
      id: language.code,
      label: language.label,
      getValue: (row) =>
        row.texts.find((text) => text.languageCode === language.code)?.name ??
        "",
    }
  }
  return columns
}

function createTranslationColumns(languages: SupportedLanguage[]) {
  return createColumns<TranslationRow>({
    enableSelection: true,
    enableSorting: true,
    enableHiding: true,
    columns: {
      // The work-queue column leads: it is what the default sort ranks by
      // and the first thing an operator scans for.
      missingCount: {
        id: "missingCount",
        label: "Missing",
        // Status string for the (toolbar) filter and grouping; the numeric
        // count still drives sorting via sortingFn so "most missing" ranks
        // highest within the missing bucket.
        getValue: (row) => (row.missingCount > 0 ? "missing" : "complete"),
        sortingFn: (a, b) => a.original.missingCount - b.original.missingCount,
        enableGrouping: true,
        getGroupingValue: (row) =>
          row.missingCount > 0 ? "Missing translations" : "Complete",
        renderGroupLabel: (value) => String(value),
        filter: {
          type: "select",
          placeholder: "Any status",
          toolbar: true,
          options: [
            { label: "Missing", value: "missing", dotVariant: "destructive" },
            { label: "Complete", value: "complete", dotVariant: "success-1" },
          ],
        },
        renderCell({ row }) {
          const count = row.original.missingCount
          return (
            <span title="Supported languages (common.Languages.IsSupported = 1) with no Name for this feature.">
              <DotText variant={count > 0 ? "destructive" : "success-1"}>
                {count > 0 ? `${count} missing` : "Complete"}
              </DotText>
            </span>
          )
        },
        summaryValue({ row }) {
          return row.original.missingCount
        },
      },
      category: {
        id: "category",
        label: "Category",
        enableGrouping: true,
        filter: { type: "text", placeholder: "Filter by category..." },
      },
      code: {
        id: "code",
        label: "Code",
        // Sorts, searches and exports on the full code — for a type value the
        // '<TableName>|<Code>' ObjectKey, which is its identity and the thing
        // an operator pastes from a seed error. Only the DISPLAY is shortened.
        renderCell({ row }) {
          const { code, shortCode } = row.original
          if (shortCode === code) return code
          // 'MUNIC', not 'location.AdministrativeAreaTypes|MUNIC': the table
          // name is repeated on every value of the set and is already the
          // row's Category, so spending the column width on it pushed the part
          // that differs out of view. Kept in the title for copying.
          return <span title={code}>{shortCode}</span>
        },
      },
      // Driven by the toolbar's Kind pills, and hidden by default for the same
      // reason as `status`: the active pill already says which kind is
      // showing. Worth having per row in the All view, where both kinds mix.
      objectType: {
        id: "objectType",
        label: "Kind",
        defaultHidden: true,
        enableGrouping: true,
        getValue: (row) => row.objectType,
        getGroupingValue: (row) => OBJECT_TYPE_LABEL_PLURAL[row.objectType],
        renderGroupLabel: (value) => String(value),
        renderCell: ({ row }) => OBJECT_TYPE_LABEL[row.original.objectType],
        summaryValue({ row }) {
          return OBJECT_TYPE_LABEL[row.original.objectType]
        },
      },
      // Driven by the toolbar's Status pills (Active / Deleted / All), not
      // a dropdown filter — see the Table.StatusFilter group below. Hidden
      // by default because the active pill already says which bucket is
      // showing; the column is there for whoever wants it per row.
      status: {
        id: "status",
        label: "Status",
        defaultHidden: true,
        getValue: (row) => (row.isDeleted ? "deleted" : "active"),
        renderCell({ row }) {
          return row.original.isDeleted ? (
            <DotText variant="destructive">Deleted</DotText>
          ) : (
            <DotText variant="success-1">Active</DotText>
          )
        },
      },
      ...createLanguageColumns(languages),
    },
  })
}

export type TranslationCatalogTableProps = Omit<
  TableProps<TranslationRow>,
  "data" | "columns"
> & {
  // Pre-filtered by the board (category / incomplete selection), so the
  // table only ever renders the rows it shows.
  rows: TranslationRow[]
  // The board's full grouping, NOT sliced like `rows`. An expanded type value's
  // attributes editor describes the shape of its whole type set, which must not
  // change depending on whether the operator got there from All texts or from
  // the Missing translations queue.
  categoryByKey: TranslationBoard["categoryByKey"]
  languages: SupportedLanguage[]
  loading: boolean
  error?: ApolloError
  // Full catalog refetch — only needed for writes whose scope the client
  // cannot enumerate (Apply to all linked features). Everything else is
  // patched straight into the Apollo cache.
  onSaved(): void
}

function TranslationCatalogTable(props: TranslationCatalogTableProps) {
  const {
    className,
    rows,
    categoryByKey,
    languages,
    loading,
    error,
    onSaved,
    ...tableProps
  } = props

  // TanStack treats the columns array's identity as configuration, so it
  // must only change when the language list does — a stable-identity
  // requirement, not a speculative optimisation.
  const columns = useMemo(
    () => createTranslationColumns(languages),
    [languages],
  )

  // Pill counts for the Status and Kind filters. Cheap: `rows` is already the
  // fully loaded in-memory catalog slice, no extra query.
  const deletedCount = useMemo(
    () => rows.filter((row) => row.isDeleted).length,
    [rows],
  )
  const activeCount = rows.length - deletedCount
  const typeValueCount = useMemo(
    () => rows.filter((row) => row.objectType === "TypeValue").length,
    [rows],
  )
  const featureCount = rows.length - typeValueCount

  const [expandedCode, setExpandedCode] = useState<string | null>(null)

  // Rows a background bulk operation is currently writing; rendered with a
  // spinner in place of the selection checkbox.
  const [busyCodes, setBusyCodes] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  function onBusyRowsChange(codes: string[], busy: boolean) {
    setBusyCodes((prev) => {
      const next = new Set(prev)
      for (const code of codes) {
        if (busy) next.add(code)
        else next.delete(code)
      }
      return next
    })
  }

  // The inline editor saves change the DB while its row stays open showing
  // the new values. Patching the cached catalog right away would re-sort
  // the table (or, in the Missing translations queue, remove the row) under
  // the operator, so the saved texts queue up here and are written into the
  // cache when that row collapses / another opens / the table unmounts.
  const client = useApolloClient()
  const pendingPatches = useRef(new Map<string, CatalogCachePatch>())
  function queuePatch(row: TranslationRow, texts: SavedTranslationText[]) {
    const existing = pendingPatches.current.get(row.code)
    if (existing) existing.texts.push(...texts)
    else
      pendingPatches.current.set(row.code, {
        code: row.code,
        objectType: row.objectType,
        texts: [...texts],
      })
  }
  const flushPatches = useRef(() => {
    if (pendingPatches.current.size === 0) return
    const patches = [...pendingPatches.current.values()]
    pendingPatches.current.clear()
    patchTranslationCatalogCache(client.cache, patches)
  })
  useEffect(() => {
    flushPatches.current()
  }, [expandedCode])
  useEffect(() => () => flushPatches.current(), [])

  errorToast(error, { title: "Failed to load translations" })

  return (
    <Table
      className={cn("translation-table", className)}
      // v2: column order is persisted per tableId once an operator has ever
      // dragged a column, which would pin the old order forever. Bumping the
      // id is a one-time layout reset that lands "Missing first" everywhere.
      tableId="translation-table-v2"
      data={rows}
      columns={columns}
      // Rows needing work float to the top: most missing languages first,
      // complete rows last (sortingFn ranks by the numeric missing count).
      initialSorting={[{ id: "missingCount", desc: true }]}
      initialColumnSizing={COLUMN_SIZING}
      getRowId={(row) => row.code}
      enableGrouping
      isCellsWrapped
      enableInfiniteScroll
      enableRowSelection
      onRowClick={(row) =>
        setExpandedCode((prev) =>
          prev === row.original.code ? null : row.original.code,
        )
      }
      busyRowIds={busyCodes}
      expandedRowId={expandedCode}
      renderRowDetail={(row) => (
        <TranslationCatalogRowDetail
          // Key by code so the draft state resets when another row opens.
          key={row.original.code}
          row={row.original}
          languages={languages}
          siblingRows={categoryByKey.get(row.original.categoryKey)?.rows ?? []}
          onSaved={onSaved}
          onSavedTexts={(texts) => queuePatch(row.original, texts)}
          onDeleted={() => {
            setExpandedCode(null)
            onSaved()
          }}
          onRenamed={() => {
            setExpandedCode(null)
            onSaved()
          }}
        />
      )}
      {...tableProps}
    >
      <Table.Toolbar>
        <Table.StatusFilter column="status" defaultValue={1}>
          <Table.StatusFilter.Option value={null} count={rows.length}>
            All
          </Table.StatusFilter.Option>
          <Table.StatusFilter.Option
            value={1}
            filterValue="active"
            count={activeCount}
          >
            Active
          </Table.StatusFilter.Option>
          <Table.StatusFilter.Option
            value={2}
            filterValue="deleted"
            variant="destructive"
            count={deletedCount}
            title="Soft-deleted rows — history is kept and they can be restored."
          >
            Deleted
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <Separator orientation="vertical" />

        {/* The split Jarle asked for: UI texts and data labels are different
            objects that happened to share one list, so scanning either meant
            reading past the other. A second pill group rather than a tab strip
            keeps every other control (search, filters, selection actions)
            pointed at the same table. */}
        <Table.StatusFilter
          column="objectType"
          urlParam={TRANSLATION_KIND_PARAM}
          defaultValue={null}
        >
          <Table.StatusFilter.Option value={null} count={rows.length}>
            All kinds
          </Table.StatusFilter.Option>
          <Table.StatusFilter.Option
            value={1}
            filterValue="Feature"
            count={featureCount}
            title="UI texts, keyed by FeatureCode."
          >
            {OBJECT_TYPE_LABEL_PLURAL.Feature}
          </Table.StatusFilter.Option>
          <Table.StatusFilter.Option
            value={2}
            filterValue="TypeValue"
            count={typeValueCount}
            title="Values of a type table (an enum), keyed by '<TableName>|<Code>'. These carry the Attributes the runtime seed reads."
          >
            {OBJECT_TYPE_LABEL_PLURAL.TypeValue}
          </Table.StatusFilter.Option>
        </Table.StatusFilter>

        <Table.Search placeholder="Search code or text..." />
        <div className="flex-1" />
        <Table.ToolbarFilters />
        <Separator orientation="vertical" />
        <Table.ToolbarMenu exportFileName="translations" />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.Content isLoading={loading} error={error} />

      <Table.SelectionToolbar<TranslationRow>>
        {(selected, reset) => (
          <>
            <TranslationAiFillAction
              selectedRows={selected}
              // The action patches the cache chunk by chunk itself; the only
              // thing left at the end is dropping the selection.
              onComplete={reset}
              onBusyRowsChange={onBusyRowsChange}
            />
            <TranslationDeleteRowsAction
              selectedRows={selected}
              onComplete={reset}
            />
          </>
        )}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default TranslationCatalogTable
